/**
 * Predictions Overview Page Component
 * Overview of all predictions/markets with statistics
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import MarketDetailModal from '../components/modals/MarketDetailModal';
import MarketOrderingPanel from '../components/MarketOrderingPanel';
import './PredictionsOverviewPage.css';

interface Market {
  id: string;
  headline: string;
  description: string;
  category: string;
  answer_type: string;
  answer_options: string[] | null;
  min_stake: number;
  max_stake: number;
  end_time_type: string;
  duration_value: number | null;
  duration_unit: string | null;
  end_date: string | null;
  payout_type: string;
  payout_after: string | null;
  payout_date: string | null;
  image_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  settled_at: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  is_featured?: boolean;
  featured_order?: number | null;
}

const PredictionsOverviewPage: React.FC = () => {
  const { setCurrentPage } = useAdmin();
  const [allMarkets, setAllMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);

  useEffect(() => {
    loadAllMarkets();
    // Track when this page is opened (set timestamp on mount)
    localStorage.setItem('last_opened_predictions-overview', new Date().toISOString());
  }, []);

  const loadAllMarkets = async () => {
    setLoading(true);
    setError(null);
    try {
      // Always load all markets to get accurate counts
      const result = await apiService.getAllMarkets();
      setAllMarkets(result?.markets || []);
    } catch (err) {
      console.error('Failed to load markets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  };

  // Filter markets based on selected tab
  const markets = statusFilter === 'all'
    ? allMarkets
    : statusFilter === 'featured'
      ? allMarkets
          .filter(m => m.is_featured === true)
          .sort((a, b) => {
            const ao = a.featured_order ?? Number.MAX_SAFE_INTEGER;
            const bo = b.featured_order ?? Number.MAX_SAFE_INTEGER;
            return ao - bo;
          })
      : allMarkets.filter(m => m.status === statusFilter);

  const getStatusCounts = () => {
    // Calculate counts from all markets, not filtered markets
    const all = allMarkets.length;
    const live = allMarkets.filter(m => m.status === 'live').length;
    const pending = allMarkets.filter(m => m.status === 'pending').length;
    const draft = allMarkets.filter(m => m.status === 'draft').length;
    const settled = allMarkets.filter(m => m.status === 'settled').length;
    const featured = allMarkets.filter(m => m.is_featured === true).length;
    return { all, live, pending, draft, settled, featured };
  };

  const handleToggleFeatured = async (market: Market, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await apiService.toggleMarketFeatured(market.id);
      setAllMarkets(prev =>
        prev.map(m =>
          m.id === market.id
            ? { ...m, is_featured: result.market.is_featured, featured_order: result.market.featured_order }
            : m
        )
      );
    } catch (err) {
      console.error('Failed to toggle featured:', err);
      alert(err instanceof Error ? err.message : 'Failed to toggle featured');
    }
  };

  const handleSetFeaturedOrder = async (market: Market, value: string) => {
    const parsed = value.trim() === '' ? null : parseInt(value, 10);
    if (parsed !== null && Number.isNaN(parsed)) return;
    try {
      const result = await apiService.setMarketFeaturedOrder(market.id, parsed);
      setAllMarkets(prev =>
        prev.map(m =>
          m.id === market.id
            ? { ...m, featured_order: result.market.featured_order }
            : m
        )
      );
    } catch (err) {
      console.error('Failed to set featured order:', err);
      alert(err instanceof Error ? err.message : 'Failed to set featured order');
    }
  };

  const formatTimeRemaining = (market: Market) => {
    let endDate: Date | null = null;
    
    if (market.end_time_type === 'date' && market.end_date) {
      endDate = new Date(market.end_date);
    } else if (market.end_time_type === 'duration' && market.duration_value && market.duration_unit && market.created_at) {
      const createdDate = new Date(market.created_at);
      let milliseconds = 0;
      switch (market.duration_unit) {
        case 'Hours':
          milliseconds = market.duration_value * 60 * 60 * 1000;
          break;
        case 'Days':
          milliseconds = market.duration_value * 24 * 60 * 60 * 1000;
          break;
        case 'Weeks':
          milliseconds = market.duration_value * 7 * 24 * 60 * 60 * 1000;
          break;
        case 'Months':
          milliseconds = market.duration_value * 30 * 24 * 60 * 60 * 1000;
          break;
      }
      endDate = new Date(createdDate.getTime() + milliseconds);
    }
    
    if (!endDate) return 'N/A';
    
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    
    if (diff < 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d|${hours}h|${minutes}m`;
    if (hours > 0) return `${hours}h|${minutes}m`;
    return `${minutes}m`;
  };

  const getAnswerOptions = (market: Market) => {
    if (market.answer_type === 'yesno') {
      return ['Yes', 'No'];
    }
    if (market.answer_type === 'multiple' && market.answer_options) {
      return market.answer_options;
    }
    return [];
  };

  const counts = getStatusCounts();

  return (
    <div className="predictions-overview-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Predictions Overview</h1>
          <p className="page-sub">
            {statusFilter === 'ordering' ? allMarkets.length : markets.length} total predictions
          </p>
        </div>
        <div className="btn-group">
          <button
            className="btn btn-secondary"
            onClick={() => setStatusFilter('ordering')}
          >
            📋 Manage Order
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setCurrentPage('create-market')}
          >
            ➕ Create Market
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All ({counts.all})
        </button>
        <button
          className={`tab ${statusFilter === 'live' ? 'active' : ''}`}
          onClick={() => setStatusFilter('live')}
        >
          Live ({counts.live})
        </button>
        <button
          className={`tab ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          Pending ({counts.pending || 0})
        </button>
        <button
          className={`tab ${statusFilter === 'draft' ? 'active' : ''}`}
          onClick={() => setStatusFilter('draft')}
        >
          Draft ({counts.draft})
        </button>
        <button
          className={`tab ${statusFilter === 'settled' ? 'active' : ''}`}
          onClick={() => setStatusFilter('settled')}
        >
          Settled ({counts.settled})
        </button>
        <button
          className={`tab ${statusFilter === 'featured' ? 'active' : ''}`}
          onClick={() => setStatusFilter('featured')}
        >
          ⭐ Featured ({counts.featured})
        </button>
        <button
          className={`tab ${statusFilter === 'ordering' ? 'active' : ''}`}
          onClick={() => setStatusFilter('ordering')}
        >
          🧭 Ordering
        </button>
      </div>

      {error && (
        <div className="empty-state">
          <p style={{ color: 'var(--red)' }}>{error}</p>
          <button
            className="btn btn-secondary"
            onClick={() => loadAllMarkets()}
            style={{ marginTop: '16px' }}
          >
            Retry
          </button>
        </div>
      )}

      {statusFilter === 'ordering' ? (
        <MarketOrderingPanel />
      ) : error ? null : loading ? (
        <div className="empty-state">
          <p>Loading predictions...</p>
        </div>
      ) : markets.length === 0 ? (
        <div className="empty-state">
          <p>No predictions found. Create your first prediction to get started.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setCurrentPage('create-market')}
            style={{ marginTop: '16px' }}
          >
            ➕ Create Market
          </button>
        </div>
      ) : (
        <div className="prediction-cards">
          {markets.map((market) => {
            const options = getAnswerOptions(market);
            const imageUrl = market.image_url || 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=400';
            const timer = formatTimeRemaining(market);
            
            return (
              <div 
                key={market.id} 
                className="prediction-card"
                onClick={() => setSelectedMarket(market)}
              >
                <div 
                  className="prediction-card-image"
                  style={{
                    backgroundImage: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%), url(${imageUrl})`
                  }}
                >
                  <div className="prediction-card-overlay">
                    <div className="prediction-timer">
                      {timer.split('|').map((t, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span>|</span>}
                          {t}
                        </React.Fragment>
                      ))}
                    </div>
                    <button 
                      className="prediction-share-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Share functionality
                      }}
                    >
                      📤
                    </button>
                  </div>
                  <div className="prediction-creator">
                    <div className="prediction-creator-avatar">
                      {(market.created_by_name || market.created_by) ? (market.created_by_name || market.created_by || 'A').charAt(0).toUpperCase() : 'A'}
                    </div>
                    <div className="prediction-creator-info">
                      <div className="prediction-creator-label">Created by</div>
                      <div className="prediction-creator-name">
                        {market.created_by_name || market.created_by || 'Admin'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="prediction-card-content">
                  <div className="prediction-engagement">
                    <div className="engagement-item">❤️ 0</div>
                    <div className="engagement-item">💬 0</div>
                    <div className="engagement-item">📤 0</div>
                    <button 
                      className="share-earn-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Share & Earn functionality
                      }}
                    >
                      Share & Earn
                    </button>
                  </div>
                  
                  <div className="prediction-title">{market.headline || 'Untitled Prediction'}</div>
                  
                  {options.length > 0 && (
                    <div className="prediction-options">
                      {options.slice(0, 4).map((option, idx) => (
                        <div key={idx} className="prediction-option">
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span className={`status ${market.status === 'live' ? 'green' : market.status === 'draft' ? 'yellow' : 'purple'}`}>
                      {market.category || 'Uncategorized'}
                    </span>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                      onClick={(e) => handleToggleFeatured(market, e)}
                      title={market.is_featured ? 'Un-feature this market' : 'Feature this market on Dashboard'}
                    >
                      {market.is_featured ? '⭐ Featured' : '☆ Feature'}
                    </button>
                    {market.is_featured && (
                      <input
                        type="number"
                        className="input"
                        style={{ width: '72px', padding: '4px 8px', fontSize: '12px' }}
                        placeholder="Order"
                        defaultValue={market.featured_order ?? ''}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const next = e.target.value;
                          const current = market.featured_order ?? '';
                          if (String(next) !== String(current)) {
                            handleSetFeaturedOrder(market, next);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MarketDetailModal
        isOpen={!!selectedMarket}
        onClose={() => setSelectedMarket(null)}
        market={selectedMarket}
      />
    </div>
  );
};

export default PredictionsOverviewPage;
